import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { getFadeColor } from '@repo/toolkit/colors'
import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import {
  CalendarOneIcon,
  CalendarTwoIcon,
  DeleteIcon,
  DuplicateIcon,
  EuroIcon,
  PenIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import NoteBox from '@repo/ui/note-box'
import Switch from '@repo/ui/switch'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import React from 'react'

import { deleteProductDate } from '../../../../api/products/delete-product-date'
import { duplicateProductDate } from '../../../../api/products/duplicate-product-date'
import {
  lockProductDate as lockProductDateApi,
  unlockProductDate as unlockProductDateApi,
  updateProductDate as updateProductDateApi
} from '../../../../api/products/update-product-date'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { useProductStore } from '../../../../lib/stores/use-product'
import {
  ProductDate,
  ProductDateProperties
} from '../../../../lib/types/models/product/product-date'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import ProductDateForm from '../product-date-form'
import { getDurationInDaysString } from '../utils'

type Props = {
  productDate: ProductDate
}

const ProductDateHeader: React.FC<Props> = ({ productDate }) => {
  const hasJourneys = productDate.journeys.length > 0
  const {
    addProductDate,
    removeProductDate,
    updateProductDate,
    lockProductDate,
    unlockProductDate
  } = useProductStore()

  const { modalState, toggleModal, closeModel } = useModalManager<
    'delete' | 'update' | 'duplicate'
  >(['delete', 'update', 'duplicate'])

  const { mutate: lockProductDateMutation, isPending: isLockProductDatePending } = useMutation({
    mutationFn: lockProductDateApi,
    onError: () => {
      unlockProductDate(productDate.id)
    }
  })

  const { mutate: unlockProductDateMutation, isPending: isUnlockProductDatePending } = useMutation(
    {
      mutationFn: unlockProductDateApi,
      onError: () => {
        lockProductDate(productDate.id)
      }
    }
  )

  const { mutateAsync: duplicateProductDateMutation } = useMutation({
    mutationFn: duplicateProductDate,
    onSuccess: (data) => {
      addProductDate(data)
      closeModel('duplicate')
    },
    onError: showAxiosError
  })

  const { mutateAsync: updateProductDateMutation } = useMutation({
    mutationFn: updateProductDateApi,
    onSuccess: (data) => {
      updateProductDate(productDate.id, data)
      closeModel('update')
    },
    onError: showAxiosError
  })

  const { mutate: deleteProductDateMutation, isPending: isDeleteProductDatePending } = useMutation(
    {
      mutationFn: deleteProductDate,
      onSuccess: () => {
        removeProductDate(productDate)
        toggleModal('delete')
      },
      onError: showAxiosError
    }
  )

  return (
    <div className="flex justify-between items-center border border-grey-light  px-2 rounded-xl shadow">
      <div className="flex">
        <div className="px-2 py-6 flex flex-col gap-1 border-r border-grey-light">
          <Typography variant="label-semibold" color={tailwindColors.grey.lighter}>
            Date de départ
          </Typography>

          <Badge
            icon={
              <CalendarTwoIcon
                width="22px"
                height="22px"
                color={ProductDateProperties.getColor(productDate)}
              />
            }
            textVariant="label-semibold"
            iconPosition="start"
            textColor={ProductDateProperties.getColor(productDate)}
            color={getFadeColor(ProductDateProperties.getColor(productDate))}
            text={DateFormatter.DDMMYYYY(new Date(productDate.departureDate))}
          />
        </div>

        <div className="px-2 py-6 flex flex-col gap-1 border-r border-grey-light">
          <Typography variant="label-semibold" color={tailwindColors.grey.lighter}>
            Date de retour
          </Typography>

          <Badge
            icon={
              <CalendarTwoIcon
                width="22px"
                height="22px"
                color={ProductDateProperties.getColor(productDate)}
              />
            }
            textVariant="label-semibold"
            iconPosition="start"
            textColor={ProductDateProperties.getColor(productDate)}
            color={getFadeColor(ProductDateProperties.getColor(productDate))}
            text={DateFormatter.DDMMYYYY(productDate.returnDate)}
          />
        </div>

        <div className="px-2 py-6 flex flex-col gap-1 border-r border-grey-light">
          <Typography variant="label-semibold" color={tailwindColors.grey.lighter}>
            Durée
          </Typography>

          <Badge
            color={tailwindColors.white.active}
            textVariant="label-semibold"
            text={getDurationInDaysString(productDate.durationInDays)}
          />
        </div>

        <div className="px-2 py-6 flex flex-col gap-1">
          <Typography variant="label-semibold" color={tailwindColors.grey.lighter}>
            Prix de base
          </Typography>

          <Badge
            textVariant="label-semibold"
            icon={<EuroIcon width="20px" height="20px" color={tailwindColors.grey.lighter} />}
            iconPosition="start"
            color={tailwindColors.white.active}
            text={`${productDate.basePrice} €`}
            className="whitespace-nowrap"
          />
        </div>
      </div>
      <div className="flex gap-2 items-center">
        <div className="border border-grey-light rounded w-full h-10 flex justify-center items-center px-2">
          <Switch
            checked={productDate.locked}
            label="Verrouillé"
            disabled={isLockProductDatePending || isUnlockProductDatePending}
            onCheckedChange={() => {
              if (productDate.locked) {
                unlockProductDate(productDate.id)
                unlockProductDateMutation(productDate.id)
              } else {
                lockProductDate(productDate.id)
                lockProductDateMutation(productDate.id)
              }
            }}
            color={tailwindColors.grey.DEFAULT}
          />
        </div>
        <ActionButton
          variant={'primary'}
          icon={DuplicateIcon}
          onClick={() => toggleModal('duplicate')}
        />
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('update')}
          disabled={productDate.locked}
        />
        <ActionButton
          variant={'danger'}
          icon={DeleteIcon}
          onClick={() => toggleModal('delete')}
          disabled={productDate.locked}
        />
      </div>

      <Modal
        actionButtonsOptions={{
          confirmText: 'Supprimer',
          cancelText: 'Fermer',
          onConfirm: () => {
            deleteProductDateMutation(productDate.id)
          }
        }}
        onCancel={() => closeModel('delete')}
        isOpen={modalState.delete}
        title={`Êtes-vous sûr de vouloir supprimer la date '${DateFormatter.DDMMYYYY(productDate.departureDate)} - ${DateFormatter.DDMMYYYY(productDate.returnDate)}' ?`}
        icon={DeleteIcon}
        isLoading={isDeleteProductDatePending}
        variant={'destructive'}
        hasBorderBottom={false}
      >
        <div className={MODAL_BODY_CLASSNAME}>
          <Typography variant="caption" className="text-center" color={tailwindColors.red.DEFAULT}>
            Vous êtes sur le point de supprimer la date. Cela va supprimer tous les séjours
            associés
          </Typography>
        </div>
      </Modal>
      <Modal
        isOpen={modalState.update}
        title={'Modifier la date'}
        icon={CalendarOneIcon}
        hideActions
        onCancel={() => closeModel('update')}
      >
        <ProductDateForm
          className={MODAL_BODY_CLASSNAME}
          disableDateFields={hasJourneys}
          defaultValues={{
            basePrice: productDate.basePrice,
            academicZones: productDate.academicZones,
            departureDate: productDate.departureDate,
            returnDate: productDate.returnDate
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('update'),
            onConfirm: async (values) => {
              await updateProductDateMutation({
                ...(hasJourneys
                  ? {
                      academicZones: values.academicZones,
                      basePrice: values.basePrice
                    }
                  : values),
                id: productDate.id,
                productId: productDate.productId
              })
              closeModel('update')
            }
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState.duplicate}
        title={'Dupliquer la date'}
        headerChildren={
          <Badge
            text={`${DateFormatter.DDMMYYYY(productDate.departureDate)} - ${DateFormatter.DDMMYYYY(productDate.returnDate)}`}
            variant="grey"
            size="small"
            textVariant="caption"
          />
        }
        icon={CalendarOneIcon}
        hideActions
        onCancel={() => closeModel('duplicate')}
      >
        <div className={MODAL_BODY_CLASSNAME}>
          <NoteBox note="Vous êtes sur le point de dupliquer la date. Cela va également dupliquer tous les séjours associés" />
        </div>
        <ProductDateForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{ basePrice: productDate.basePrice }}
          buttonsConfig={modalButtonConfig({
            confirmText: 'Dupliquer',
            onCancel: () => closeModel('duplicate'),
            onConfirm: async (values) => {
              await duplicateProductDateMutation({
                ...values,
                id: productDate.id,
                productId: productDate.productId
              })
            }
          })}
        />
      </Modal>
    </div>
  )
}

export default ProductDateHeader
